import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.75;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  #lastKnownItemCount = 0;

  /** @type {AbortController | null} */
  #checkoutJumpAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handleCheckoutJump);

    this.#lastKnownItemCount = this.#readCartBubbleCount();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleCheckoutJump);
    this.#historyAbortController?.abort();
    this.#checkoutJumpAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    this.#toggleFloatingCheckout(false);
    requestAnimationFrame(() => this.#bindCheckoutJumpVisibility());
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    this.#checkoutJumpAbortController?.abort();
    if (this.refs.dialog) delete this.refs.dialog.dataset.checkoutVisible;
    this.#setAddedState(false);
    if (this.#lastKnownItemCount > 0) {
      this.#toggleFloatingCheckout(true);
    }
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };


  #handleCheckoutJump = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-clingy-scroll-checkout]');
    if (!button) return;

    event.preventDefault();

    const dialog = this.refs.dialog;
    const content = dialog?.querySelector('.cart-drawer__content');
    const checkoutActions = dialog?.querySelector('[data-clingy-checkout-actions]');
    const checkoutSection = dialog?.querySelector('[data-clingy-checkout-section]');
    const targetSection = checkoutActions || checkoutSection;

    if (targetSection instanceof HTMLElement) {
      targetSection.scrollIntoView({ behavior: 'smooth', block: 'end' });
      window.setTimeout(() => this.#updateCheckoutJumpVisibility(), 450);
    } else if (content instanceof HTMLElement) {
      content.scrollTo({ top: content.scrollHeight, behavior: 'smooth' });
      window.setTimeout(() => this.#updateCheckoutJumpVisibility(), 450);
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const source = event.detail?.data?.source;
    const didError = event.detail?.data?.didError === true;
    const cartCount = event.detail?.resource?.item_count;
    if (typeof cartCount === 'number') {
      this.#lastKnownItemCount = cartCount;
    }

    const shouldOpenAfterExplicitAdd = !didError && [
      'product-form-component',
      'clingy-qty-selector',
      'clingy-cart-upsell',
    ].includes(source);

    if (this.hasAttribute('auto-open') || shouldOpenAfterExplicitAdd) {
      this.#setAddedState(true);
      this.#toggleFloatingCheckout(false);
      this.showDialog();
      requestAnimationFrame(() => {
        this.#setAddedState(true);
        this.#updateStickyState();
        this.#bindCheckoutJumpVisibility();
      });
    }

    this.#announceCartCount(event.detail?.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.#lastKnownItemCount = Math.max(this.#lastKnownItemCount, this.#readCartBubbleCount());
    this.#setAddedState(false);
    this.#toggleFloatingCheckout(false);
    this.showDialog();
    requestAnimationFrame(() => this.#bindCheckoutJumpVisibility());

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #readCartBubbleCount() {
    const countEl = document.querySelector('[data-testid="cart-bubble"]');
    const count = Number(countEl?.textContent?.trim() || 0);
    return Number.isFinite(count) ? count : 0;
  }

  #setAddedState(isAdded) {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    const headingCopy = dialog.querySelector('[data-cart-drawer-heading-copy]');
    if (headingCopy) {
      headingCopy.textContent = isAdded ? 'Added to Cart' : 'Your Cart';
    }

    if (isAdded) {
      dialog.dataset.cartAdded = 'true';
    } else {
      delete dialog.dataset.cartAdded;
    }
  }

  #toggleFloatingCheckout(show) {
    const floatingCheckout = document.querySelector('[data-clingy-floating-checkout]');
    if (!(floatingCheckout instanceof HTMLElement)) return;

    if (show && this.#lastKnownItemCount > 0 && !this.refs.dialog?.open) {
      floatingCheckout.hidden = false;
      requestAnimationFrame(() => floatingCheckout.classList.add('is-visible'));
      return;
    }

    floatingCheckout.classList.remove('is-visible');
    window.setTimeout(() => {
      if (!floatingCheckout.classList.contains('is-visible')) {
        floatingCheckout.hidden = true;
      }
    }, 230);
  }

  #bindCheckoutJumpVisibility() {
    const dialog = this.refs.dialog;
    const content = dialog?.querySelector('.cart-drawer__content');
    const jumpButton = dialog?.querySelector('[data-clingy-scroll-checkout]');

    this.#checkoutJumpAbortController?.abort();

    if (!(dialog instanceof HTMLDialogElement) || !dialog.open || !(content instanceof HTMLElement) || !(jumpButton instanceof HTMLElement)) {
      if (dialog) delete dialog.dataset.checkoutVisible;
      return;
    }

    this.#checkoutJumpAbortController = new AbortController();
    const { signal } = this.#checkoutJumpAbortController;
    content.addEventListener('scroll', this.#updateCheckoutJumpVisibility, { passive: true, signal });
    window.addEventListener('resize', this.#updateCheckoutJumpVisibility, { passive: true, signal });
    this.#updateCheckoutJumpVisibility();
  }

  #updateCheckoutJumpVisibility = () => {
    const dialog = this.refs.dialog;
    const content = dialog?.querySelector('.cart-drawer__content');
    const checkoutActions = dialog?.querySelector('[data-clingy-checkout-actions]');
    const checkoutSection = dialog?.querySelector('[data-clingy-checkout-section]');
    const targetSection = checkoutActions || checkoutSection;

    if (!(dialog instanceof HTMLDialogElement) || !(content instanceof HTMLElement) || !(targetSection instanceof HTMLElement)) {
      if (dialog) delete dialog.dataset.checkoutVisible;
      return;
    }

    const contentRect = content.getBoundingClientRect();
    const targetRect = targetSection.getBoundingClientRect();
    const actionsAreVisible = targetRect.top < contentRect.bottom - 70 && targetRect.bottom > contentRect.top + 20;
    const nearBottom = content.scrollTop + content.clientHeight >= content.scrollHeight - 24;

    if (actionsAreVisible || nearBottom) {
      dialog.dataset.checkoutVisible = 'true';
    } else {
      delete dialog.dataset.checkoutVisible;
    }
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
